import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import type { AdminWishlistPayload, Gift } from "@wishlist-hub/shared";
import { Button, Card, ErrorText, Input, Label, TextArea } from "../components/ui";
import { api } from "../services/api";

const giftSchema = z.object({
  title: z.string().min(1).max(200),
  description: z.string().max(1000).optional(),
  price: z.coerce.number().positive().optional().or(z.nan().transform(() => undefined)),
  url: z.string().url().optional().or(z.literal("")),
  imageUrl: z.string().url().optional().or(z.literal("")),
  quantity: z.coerce.number().int().positive(),
  priority: z.coerce.number().int().min(0).max(1),
});

const settingsSchema = z.object({
  title: z.string().min(1).max(100),
  description: z.string().max(500).optional(),
  isPrivate: z.boolean(),
});

export function WishlistAdmin() {
  const { adminToken } = useParams<{ adminToken: string }>();
  const [payload, setPayload] = useState<AdminWishlistPayload | null>(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState<Gift | null>(null);

  const adminHeader = { headers: { "X-Admin-Token": adminToken ?? "" } };

  const load = () => {
    if (!adminToken) return;
    api
      .get<AdminWishlistPayload>(`/admin/wishlists/${adminToken}`)
      .then(({ data }) => setPayload(data))
      .catch(() => setError("Неверная админ-ссылка"));
  };

  useEffect(() => {
    load();
  }, [adminToken]);

  useEffect(() => {
    if (!payload?.gifts.length) return;
    const id = window.location.hash.replace(/^#/, "");
    if (!id.startsWith("gift-")) return;
    requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });
  }, [payload?.gifts]);

  const settingsForm = useForm<z.infer<typeof settingsSchema>>({
    resolver: zodResolver(settingsSchema),
  });

  useEffect(() => {
    if (payload) {
      settingsForm.reset({
        title: payload.wishlist.title,
        description: payload.wishlist.description ?? "",
        isPrivate: payload.wishlist.isPrivate,
      });
    }
  }, [payload]);

  const giftForm = useForm<z.infer<typeof giftSchema>>({
    resolver: zodResolver(giftSchema),
    defaultValues: {
      title: "",
      quantity: 1,
      priority: 0,
    },
  });

  useEffect(() => {
    if (editing) {
      giftForm.reset({
        title: editing.title,
        description: editing.description ?? "",
        price: editing.price ?? undefined,
        url: editing.url ?? "",
        imageUrl: editing.imageUrl ?? "",
        quantity: editing.quantity,
        priority: editing.priority,
      });
    } else {
      giftForm.reset({
        title: "",
        description: "",
        quantity: 1,
        priority: 0,
        url: "",
        imageUrl: "",
      });
    }
  }, [editing]);

  if (error && !payload) return <p className="text-red-400">{error}</p>;
  if (!payload) return <p className="text-slate-400">Загрузка…</p>;

  const { wishlist, gifts, reservations } = payload;
  const publicUrl = `${window.location.origin}/wishlist/${wishlist.slug}`;

  const saveSettings = settingsForm.handleSubmit(async (v) => {
    if (!adminToken) return;
    await api.put(`/admin/wishlists/${adminToken}`, {
      title: v.title,
      description: v.description || null,
      isPrivate: v.isPrivate,
    });
    load();
  });

  const saveGift = giftForm.handleSubmit(async (v) => {
    if (!adminToken) return;
    const body = {
      title: v.title,
      description: v.description || undefined,
      price: v.price,
      url: v.url || undefined,
      imageUrl: v.imageUrl || undefined,
      quantity: v.quantity,
      priority: v.priority as 0 | 1,
    };
    if (editing) {
      await api.put(`/admin/gifts/${editing.id}`, body, adminHeader);
    } else {
      await api.post(`/admin/wishlists/${adminToken}/gifts`, body);
    }
    setEditing(null);
    load();
  });

  const removeGift = async (g: Gift) => {
    if (!adminToken || !confirm("Удалить подарок?")) return;
    await api.delete(`/admin/gifts/${g.id}`, adminHeader);
    load();
  };

  const removeReservation = async (id: string) => {
    if (!adminToken || !confirm("Снять бронь?")) return;
    await api.delete(`/admin/reservations/${id}`, adminHeader);
    load();
  };

  return (
    <>
      <Helmet>
        <title>Админ — {wishlist.title}</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <div className="space-y-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Админка списка</h1>
          <p className="mt-1 text-sm text-slate-500 break-all">Публичная ссылка: {publicUrl}</p>
          <Button
            variant="secondary"
            className="mt-2"
            onClick={() => navigator.clipboard.writeText(publicUrl)}
          >
            Копировать публичную ссылку
          </Button>
        </div>

        <Card>
          <h2 className="font-semibold text-white">Настройки</h2>
          <form className="mt-4 space-y-3" onSubmit={saveSettings}>
            <div>
              <Label>Название</Label>
              <Input {...settingsForm.register("title")} />
            </div>
            <div>
              <Label>Описание</Label>
              <TextArea rows={2} {...settingsForm.register("description")} />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-300">
              <input type="checkbox" {...settingsForm.register("isPrivate")} className="rounded border-slate-600" />
              Режим сюрприз
            </label>
            <Button type="submit" disabled={settingsForm.formState.isSubmitting}>
              Сохранить
            </Button>
          </form>
        </Card>

        <Card>
          <h2 className="font-semibold text-white">{editing ? "Редактировать подарок" : "Новый подарок"}</h2>
          <form className="mt-4 grid gap-3 sm:grid-cols-2" onSubmit={saveGift}>
            <div className="sm:col-span-2">
              <Label>Название</Label>
              <Input {...giftForm.register("title")} />
            </div>
            <div className="sm:col-span-2">
              <Label>Описание</Label>
              <TextArea rows={2} {...giftForm.register("description")} />
            </div>
            <div>
              <Label>Цена</Label>
              <Input type="number" step="0.01" {...giftForm.register("price")} />
            </div>
            <div>
              <Label>Количество</Label>
              <Input type="number" {...giftForm.register("quantity")} />
            </div>
            <div>
              <Label>URL товара</Label>
              <Input {...giftForm.register("url")} />
            </div>
            <div>
              <Label>URL картинки</Label>
              <Input {...giftForm.register("imageUrl")} />
            </div>
            <div>
              <Label>Приоритет (0/1)</Label>
              <Input type="number" min={0} max={1} {...giftForm.register("priority")} />
            </div>
            <div className="sm:col-span-2 flex gap-2">
              <Button type="submit">{editing ? "Сохранить" : "Добавить"}</Button>
              {editing && (
                <Button type="button" variant="secondary" onClick={() => setEditing(null)}>
                  Отмена
                </Button>
              )}
            </div>
          </form>
        </Card>

        <div>
          <h2 className="mb-3 font-semibold text-white">Подарки</h2>
          <ul className="space-y-2">
            {gifts.map((g) => (
              <li
                key={g.id}
                id={`gift-${g.id}`}
                className="flex flex-wrap items-center justify-between gap-2 scroll-mt-24 rounded-lg border border-slate-800 bg-slate-900/50 px-3 py-2"
              >
                <span className="text-slate-200">
                  {g.title}{" "}
                  <span className="text-slate-500 text-sm">
                    ({g.reservedCount ?? 0}/{g.quantity} бронь)
                  </span>
                </span>
                <div className="flex gap-2">
                  <Button variant="ghost" className="!py-1 !px-2" onClick={() => setEditing(g)}>
                    Изменить
                  </Button>
                  <Button variant="danger" className="!py-1 !px-2" onClick={() => removeGift(g)}>
                    Удалить
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h2 className="mb-3 font-semibold text-white">Бронирования</h2>
          {reservations.length === 0 ? (
            <p className="text-slate-500 text-sm">Пока нет</p>
          ) : (
            <ul className="space-y-2">
              {reservations.map((r) => (
                <li
                  key={r.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-800 px-3 py-2 text-sm"
                >
                  <span className="text-slate-300">
                    {r.guestName} ×{r.quantity}
                    {r.guestEmail && <span className="text-slate-500"> · {r.guestEmail}</span>}
                  </span>
                  <Button variant="ghost" className="!py-1 !px-2" onClick={() => removeReservation(r.id)}>
                    Снять
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </>
  );
}
